import { useMemo } from 'react'
import QR from 'qrcode'

export interface QrCodeProps {
  /** The payload the code encodes. Scanners read exactly this string. */
  readonly value: string
  /** Rendered edge length in pixels, quiet zone included. */
  readonly size?: number
  /** Modules of empty margin. Four is the minimum the spec asks for. */
  readonly quietZone?: number
  readonly label?: string
}

/**
 * A real, scannable QR code. The matrix comes from a spec-compliant encoder and
 * is drawn as one SVG path, so it inherits `currentColor` and stays crisp at any
 * size. Error correction is level M — it survives a smudged screen or a bad angle.
 */
export function QrCode({ value, size = 168, quietZone = 4, label }: QrCodeProps) {
  const path = useMemo(() => {
    const { modules } = QR.create(value, { errorCorrectionLevel: 'M' })
    const count = modules.size
    const span = count + quietZone * 2
    const parts: string[] = []

    for (let y = 0; y < count; y += 1) {
      let run = 0
      for (let x = 0; x <= count; x += 1) {
        const dark = x < count && modules.data[y * count + x] === 1
        if (dark) {
          run += 1
          continue
        }
        if (run > 0) {
          parts.push(`M${x - run + quietZone} ${y + quietZone}h${run}v1h-${run}z`)
          run = 0
        }
      }
    }

    return { d: parts.join(''), span }
  }, [value, quietZone])

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${path.span} ${path.span}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={label ?? 'QR code'}
      style={{ borderRadius: 'var(--pb-radius-lg)', display: 'block' }}
    >
      {/* Pure black on white, not brand tokens: a scanner needs the contrast, and
          CSS variables do not resolve once the SVG is serialised or saved. */}
      <rect width={path.span} height={path.span} fill="#ffffff" />
      <path d={path.d} fill="#000000" />
    </svg>
  )
}
